/* eslint-disable react/jsx-one-expression-per-line */
import React, { useContext, useState } from "react";
import { withRouter } from "react-router-dom";
import _ from "lodash";
import AppContext from "../../AppContext";
import {
  H3,
  FlexBox,
  SpinnerIcon,
  IconPlaceholder,
  InfoParagraph,
  SpacingBox,
  FluidPrimaryButton,
  Paragraph,
  TextOnlyUnderlinedButton,
} from "../../Components";
import { getTranslateByScope } from "../../i18n";
import styles from "./CloseOrder.module.scss";
import { formatMoney } from "../../utils/money";
import {
  isOrderEditable,
  hasOrderFailedToSync,
  handleSubmitOrder,
  isOrderDone,
} from "../../utils/order";
import {
  getCurrentOrder,
  getCustomerForId,
  getPriceType,
  getProductForNumber,
} from "../../selectors";
import AddressDropdown from "../AddressDropdown";
import { priceTypes } from "../../constants";
import OrderTotalEffect from "../OrderTotalEffect";
import ConfirmModal from "../ConfirmModal";
import { QuantitySummary } from "./CloseOrder/QuantitySummary";
import SetDiscount from "./CloseOrder/SetDiscount";
import LinkToSalesforce from "../../Components/LinkToSalesforce";
import { requiresMinQuantity } from "../../utils/products";

const TotalText = H3;
const translate = getTranslateByScope("app.closeOrder");

export const Layout = ({ children }) => (
  <SpacingBox paddingLeft="md" paddingRight="md" className={styles.layout}>
    {children}
  </SpacingBox>
);

export const Item = (props) => (
  <FlexBox
    justifyContent="space-between"
    flexWrap="wrap"
    marginTop="md"
    marginBottom="md"
    alignItems={props.alignItems || "center"}
  >
    {props.children}
  </FlexBox>
);

export const CloseOrder = withRouter(({ shouldRender, history }) => {
  const context = useContext(AppContext);
  const currentOrder = getCurrentOrder(context);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState();
  const [loadingSpinner, setLoadingSpinner] = useState();

  const { totalAfterDiscounts, initialLoading, orderData, appliedDiscount } =
    OrderTotalEffect({ shouldRender, order: currentOrder });

  const updateOrderData = (data) =>
    context.actions.updateOrder(currentOrder.id, {
      ...currentOrder.doc,
      ...data,
    });

  const updateDiscount = (discounts) => {
    const discount1 = discounts.a;
    const discount2 = discounts.b;
    const discount3 = discounts.c;
    const discount = discounts.total;

    context.actions.updateOrder(currentOrder.id, {
      ...currentOrder.doc,
      discount: discount,
      discount1: discount1,
      discount2: discount2,
      discount3: discount3,
    });
  };

  const isReadonly = !isOrderEditable(_.get(currentOrder, "doc"));
  const failedToSync = hasOrderFailedToSync(_.get(currentOrder, "doc"));
  const isDone = isOrderDone(_.get(currentOrder, "doc"));

  const customer = getCustomerForId(context, currentOrder.doc.customerId);

  const addLoadingSpinner = () => setLoadingSpinner(true);
  const removeLoadingSpinner = () => setLoadingSpinner(false);

  const validate = (order) => {
    const products = order.doc.products;
    const orderType = order.doc.orderType.value;

    const productKeys = Object.keys(products);

    for (const k of productKeys) {
      const p = products[k];
      const data = p.data;
      if (data === undefined) {
        continue;
      }

      const productInfo = getProductForNumber(context)(p.number);

      const sizes = Object.keys(p.data);
      // collection 99 is accessories, no min quantity validation needed
      const minQuantityProduct = requiresMinQuantity(
        productInfo.cl,
        productInfo.col,
        sizes,
        orderType,
      );

      if (!minQuantityProduct) {
        continue;
      }

      for (const size of sizes) {
        if (size !== "E") {
          continue;
        }

        const colors = Object.keys(data[size]) ?? [];
        for (const color of colors) {
          const amount = data[size][color]["E"] ?? 0;
          console.log("amt: ", amount);

          if (amount > 0 && amount < 4) {
            console.log("min quantity");
            return false;
          }
        }
      }
    }

    return true;
  };

  if (!customer) return null;
  return (
    <Layout>
      <div>
        <b>{translate("customerNo")}:</b>
        <br />
        <LinkToSalesforce obj={customer} /> {customer.Name} -{" "}
        {customer.CustomerNo__c}
      </div>
      <div>
        <b>{translate("orderNo")}:</b>
        <br />
        {currentOrder.doc.id}
      </div>
      <QuantitySummary orderData={currentOrder.doc} />
      <SpacingBox marginTop="lg">
        <AddressDropdown
          customer={customer}
          isDisabled={isReadonly}
          onChange={(deliveryAddress) => updateOrderData({ deliveryAddress })}
          address={currentOrder.doc.deliveryAddress}
          translations={{
            title: translate("deliveryAddress"),
            editTitle: translate("editDeliveryAddress"),
          }}
        />
      </SpacingBox>
      <Item>
        <InfoParagraph>{translate("lvp")}</InfoParagraph>
        <InfoParagraph>
          {orderData.total
            ? formatMoney(orderData.total, orderData.currency)
            : ""}
        </InfoParagraph>
      </Item>
      {initialLoading ? <SpinnerIcon spinning /> : <IconPlaceholder />}
      <Item alignItems="top">
        <InfoParagraph>{translate("discount")}</InfoParagraph>
        <>
          <div style={{ textAlign: "right" }}>
            - {formatMoney(appliedDiscount ?? 0, orderData.currency)}
          </div>
        </>
      </Item>
      <SetDiscount
        currency={orderData.currency}
        total={orderData.total}
        order={currentOrder.doc}
        updateDiscount={updateDiscount}
      />
      <Item></Item>
      <Item>
        <TotalText>{translate("total")}</TotalText>
        {orderData.total ? totalAfterDiscounts : translate("emptyCart")}
      </Item>
      {(!isReadonly || failedToSync) && (
        <FluidPrimaryButton
          onClick={() => {
            if (validate(currentOrder)) {
              setIsOpenConfirmModal(true);
            } else {
              context.actions.setToast({
                title: translate("minQuantityRequired"),
                text: translate("minQuantityText"),
                timeout: 6000,
              });
              console.log("failed");
            }
          }}
          text={failedToSync ? translate("syncButton") : translate("buyButton")}
        />
      )}
      <ConfirmModal
        isOpen={isOpenConfirmModal}
        setIsOpen={setIsOpenConfirmModal}
        onConfirm={() => {
          addLoadingSpinner();
          handleSubmitOrder(
            currentOrder,
            context,
            history,
            removeLoadingSpinner,
            removeLoadingSpinner,
          );
        }}
        title={translate("confirmModal.title")}
        buttonCaption={translate("confirmModal.buttonCaption")}
        description={translate("confirmModal.description")}
      />
      {!!loadingSpinner && (
        <FlexBox
          flex={1}
          marginTop="md"
          justifyContent="space-between"
          alignItems="center"
        >
          <SpinnerIcon /> <Paragraph>{translate("submitting")}</Paragraph>
        </FlexBox>
      )}
      {!!isDone && <Paragraph>{translate("submittedToSalesForce")}</Paragraph>}
      {!isDone && getPriceType(context) === "m" && (
        <SpacingBox marginTop="lg">
          <Paragraph>{translate("msrpModeWarning")}</Paragraph>
          <TextOnlyUnderlinedButton
            onClick={() => {
              context.actions.changePriceType(priceTypes.unit);
              window.location.reload();
            }}
            text={translate("changeToUnit")}
          />
        </SpacingBox>
      )}
    </Layout>
  );
});

export default CloseOrder;
